import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui';

export interface ArtistArchiveModalProps {
  isOpen: boolean;
  artistName: string;
  productCount: number;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ArtistArchiveModal = ({
  isOpen,
  artistName,
  productCount,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: ArtistArchiveModalProps) => {
  const { t } = useTranslation('artists');
  const blocked = productCount > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="artist-archive-modal" className="max-w-md">
        <DialogTitle>{t('artists.archive.title')}</DialogTitle>
        <DialogDescription>
          {t('artists.archive.subject', { name: artistName })}
        </DialogDescription>

        {blocked ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-3 text-sm"
            style={{
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
            }}
          >
            {t('artists.archive.blocked', { count: productCount })}
          </p>
        ) : (
          <p className="text-sm" style={{ color: 'var(--content-default)' }}>
            {t('artists.archive.confirm')}
          </p>
        )}

        {errorMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-2 text-sm"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('artists.actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onConfirm}
            disabled={isSubmitting || blocked}
          >
            {isSubmitting
              ? t('artists.archive.submitting')
              : t('artists.actions.archive')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
